/**
 * Admin dashboard — mock data for the sketch.
 *
 * TODO(backend): every export below is prototype-only. When the backend is
 *   wired up, replace each of these with an API call and short-circuit to
 *   the mock when `isDemoMode()` returns true. See /app/HANDOFF.md §3.
 *
 * Board-meeting posture (2026-08-17): the sample data is hidden by default
 * so a board member clicking around the admin sees the STRUCTURE without
 * being misled by fake payouts, fake verifications, and fake donation
 * counters. Flip SHOW_MOCK_ADMIN_DATA to true for internal previews of
 * the "populated" state (e.g., product review sessions with the founder).
 * The empty-state paths inside each admin component already handle the
 * zero-data case — no component-level changes needed.
 */

import { CAMPAIGNS } from "./content";

// ---------------------------------------------------------------------------
// Feature flag — controls whether the admin dashboard renders the sample
// data below, or empty arrays / zero metrics. Default: OFF for board demo.
// Flip to true when internally reviewing the "populated" admin experience.
// ---------------------------------------------------------------------------
const SHOW_MOCK_ADMIN_DATA = false;

// Empty-state defaults, mirrored to the shapes below so any admin surface
// that destructures fields still works when we're in board-safe mode.
const EMPTY_ADMIN_METRICS = {
  campaignsClosingThisWeek: 0,
  daysToVerifyAvg: 0,
  pendingVerificationCount: 0,
  activeCampaignCount: 0,
  disbursedThisMonth: 0,
  totalDisbursed: 0,
  studentsHelped: 0,
  averageGift: 0,
  averageRequest: 0,
  topDonorMonth: null,
  topDonorYear: null,
};

// ---------------------------------------------------------------------------
// Running / at-a-glance metrics (surfaces on the Overview page and later on
// the public landing page — see user requirement 2026-02-25).
// ---------------------------------------------------------------------------
const _MOCK_ADMIN_METRICS = {
  // "This week"
  campaignsClosingThisWeek: 3,
  daysToVerifyAvg: 1.2,
  pendingVerificationCount: 5,
  activeCampaignCount: 12,
  disbursedThisMonth: 18420,

  // Lifetime — will move to landing hero counters
  totalDisbursed: 128740,
  studentsHelped: 63,
  averageGift: 82,
  averageRequest: 1780,

  // Top donors
  topDonorMonth: { name: "The Bell Family", amount: 5000 },
  topDonorYear: { name: "Codrington Foundation", amount: 42500 },
};

export const ADMIN_METRICS = SHOW_MOCK_ADMIN_DATA ? _MOCK_ADMIN_METRICS : EMPTY_ADMIN_METRICS;

// ---------------------------------------------------------------------------
// Verification queue — the operational core of the dashboard.
// ---------------------------------------------------------------------------
// Objectionable-word check list (mock — the real list will be maintained by
// ops and can be extended into an automated scan post-integration).
export const OBJECTIONABLE_WORDS_SAMPLE = [
  "hate", "slur-example", "weapon", "revenge",
];

const _MOCK_PENDING_VERIFICATIONS = [
  {
    id: "v-001",
    name: "Priya S.",
    school: "Washington State University",
    major: "Neuroscience, Class of '27",
    email: "priya.s@wsu.edu",
    submittedAt: "2026-02-24",
    status: "pending", // pending | in-review | approved | rejected | info-requested
    isReturningStudent: false,
    annualRaisedToDate: 0,
    annualCap: 10000,
    goal: 1500,
    category: "Test Prep",
    story: "MCAT prep course + AAMC bundle — I've saved half through weekend tutoring and need the rest to sit the summer exam.",
    documents: [
      { name: "NSC_enrollment_verification.pdf", uploadedAt: "2026-02-24" },
      { name: "Student_ID.jpg", uploadedAt: "2026-02-24" },
    ],
    infoRequests: [],
    // Trust signals ------------------------------------------------
    isEduEmail: true,
    studentCertifiedAt: "2026-02-24T14:12:00Z",
    nscStatus: "not-attempted", // updated to "verified" | "not-found" | "unavailable" once checked
    nscLastCheckedAt: null,
    nscConfidence: null,
    nscReasonText: null,
  },
  {
    id: "v-002",
    name: "Ethan L.",
    school: "St. John's University — MN",
    major: "Environmental Studies, Class of '28",
    email: "ethan.l@csbsju.edu",
    submittedAt: "2026-02-23",
    status: "in-review",
    isReturningStudent: false,
    annualRaisedToDate: 0,
    annualCap: 10000,
    goal: 620,
    category: "Winter Gear",
    story: "First winter away from Phoenix. I need a proper coat and boots to get across campus safely in January.",
    documents: [
      { name: "Enrollment_letter_SJU.pdf", uploadedAt: "2026-02-23" },
    ],
    infoRequests: [],
    isEduEmail: true,
    nscStatus: "not-attempted", nscLastCheckedAt: null, nscConfidence: null, nscReasonText: null,
  },
  {
    id: "v-003",
    name: "Marcus J.",
    school: "Prairie View A&M University",
    major: "Pre-Med, Class of '27",
    email: "marcus.j@pvamu.edu",
    submittedAt: "2026-02-22",
    status: "info-requested",
    isReturningStudent: true,
    annualRaisedToDate: 1200,
    annualCap: 10000,
    goal: 900,
    category: "Books & MCAT",
    story: "MCAT prep + textbooks for my final pre-med semester.",
    documents: [
      { name: "Course_registration.pdf", uploadedAt: "2026-02-22" },
    ],
    infoRequests: [
      {
        at: "2026-02-23",
        by: "Terri",
        message: "Please upload proof of current-semester enrollment (Registrar letter or NSC report).",
        awaitingReply: true,
      },
    ],
    isEduEmail: true, studentCertifiedAt: "2026-02-22T11:05:00Z",
    nscStatus: "not-attempted", nscLastCheckedAt: null, nscConfidence: null, nscReasonText: null,
  },
  {
    id: "v-004",
    name: "Jamal R.",
    school: "Cal State Dominguez Hills",
    major: "Communications, Class of '27",
    email: "jamal.rivera@gmail.com",   // <- deliberately NOT .edu, to show the trust-signal contrast
    submittedAt: "2026-02-22",
    status: "pending",
    isReturningStudent: false,
    annualRaisedToDate: 0,
    annualCap: 10000,
    goal: 1200,
    category: "Emergency Housing",
    story: "Family lost their apartment. Need help with a security deposit so I can stay near campus this semester.",
    documents: [
      { name: "Enrollment_confirm_CSUDH.pdf", uploadedAt: "2026-02-22" },
      { name: "Financial_aid_award.pdf", uploadedAt: "2026-02-22" },
    ],
    infoRequests: [],
    isEduEmail: false, studentCertifiedAt: "2026-02-22T18:44:00Z",
    nscStatus: "not-attempted", nscLastCheckedAt: null, nscConfidence: null, nscReasonText: null,
  },
  {
    id: "v-005",
    name: "Sarah T.",
    school: "Brown University",
    major: "History, Class of '27",
    email: "sarah.t@brown.edu",
    submittedAt: "2026-02-21",
    status: "pending",
    isReturningStudent: false,
    annualRaisedToDate: 0,
    annualCap: 10000,
    goal: 850,
    category: "Winter Gear",
    story: "Providence winters are longer than I planned for. Need proper cold-weather gear + boots.",
    documents: [
      { name: "Enrollment_verification_Brown.pdf", uploadedAt: "2026-02-21" },
    ],
    infoRequests: [],
    isEduEmail: true, studentCertifiedAt: "2026-02-21T15:30:00Z",
    nscStatus: "not-attempted", nscLastCheckedAt: null, nscConfidence: null, nscReasonText: null,
  },
];

// ---------------------------------------------------------------------------
// Post-approval enrollment changes — campaigns that went live, then had a
// registrar / financial-aid status change detected against them. Requires
// explicit admin decision (never auto-suspended per policy).
//
// severity:
//   "compassionate"           -> financial hold / non-payment. Lean toward
//                                keeping the campaign open unless deception
//                                is evident. Marked warm-tone in UI.
//   "recommended-suspension"  -> academic standing failure. Suspend unless
//                                admin finds reason to keep it live. Marked
//                                hard-tone in UI.
// ---------------------------------------------------------------------------
const _MOCK_ENROLLMENT_CHANGES = [
  {
    id: "ec-001",
    campaignId: 0, // Maya R. (first in CAMPAIGNS)
    studentName: "Maya R.",
    school: "Stanford University",
    detectedAt: "2026-02-24",
    reason: "non-payment",
    reasonText: "Financial hold placed on account. Spring 2026 registration blocked. Source: NSC monthly delta.",
    severity: "compassionate",
    reviewStatus: "pending", // pending | resolved-keep | resolved-suspend
    raisedSoFar: 1680,
    goal: 2000,
  },
  {
    id: "ec-002",
    campaignId: 1, // Andre W.
    studentName: "Andre W.",
    school: "Howard University",
    detectedAt: "2026-02-23",
    reason: "academic-standing",
    reasonText: "Cumulative GPA fell below program minimum. Placed on academic probation for Spring 2026.",
    severity: "recommended-suspension",
    reviewStatus: "pending",
    raisedSoFar: 940,
    goal: 1800,
  },
  {
    id: "ec-003",
    campaignId: 4, // whoever is idx 4 in CAMPAIGNS
    studentName: "Zohran M.",
    school: "UC Berkeley",
    detectedAt: "2026-02-25",
    reason: "withdrawn",
    reasonText: "Student withdrew from Spring 2026 term. Reason not disclosed by school.",
    severity: "recommended-suspension",
    reviewStatus: "pending",
    raisedSoFar: 380,
    goal: 900,
  },
];

// ---------------------------------------------------------------------------
// Grants queue — campaigns whose funds are ready for grant disbursement
// directly to the verified student. School is context only, not a payee.
// ---------------------------------------------------------------------------
const _MOCK_PAYOUTS = [
  { id: "p-001", student: "Aisha K.", school: "Spelman College",   amount: 1980, closedAt: "2026-02-24", status: "ready" },
  { id: "p-002", student: "David R.", school: "UT Austin",         amount: 1450, closedAt: "2026-02-23", status: "ready" },
  { id: "p-003", student: "Maya R.",  school: "Stanford University", amount: 2000, closedAt: "2026-02-20", status: "processing" },
  { id: "p-004", student: "Andre W.", school: "Howard University", amount: 940,  closedAt: "2026-02-18", status: "sent" },
];

// ---------------------------------------------------------------------------
// Recent donations — used on the Overview page snapshot and in the Donations
// & Payouts list view. Real transactions live in the Donations feed once
// backend is wired.
// ---------------------------------------------------------------------------
const _MOCK_RECENT_DONATIONS = [
  { id: "d-101", donor: "Anonymous", amount: 100, campaign: "Maya R.", school: "Stanford", at: "2026-02-25" },
  { id: "d-102", donor: "The Bell Family", amount: 250, campaign: "Maya R.", school: "Stanford", at: "2026-02-25" },
  { id: "d-103", donor: "K. Sparks", amount: 150, campaign: "Andre W.", school: "Howard", at: "2026-02-25" },
  { id: "d-104", donor: "M. Chen", amount: 50, campaign: "Priya S.", school: "WSU", at: "2026-02-24" },
  { id: "d-105", donor: "Anonymous", amount: 80, campaign: "Zohran M.", school: "UC Berkeley", at: "2026-02-24" },
  { id: "d-106", donor: "Codrington Foundation", amount: 500, campaign: "Sarah T.", school: "Brown", at: "2026-02-24" },
];

// ---------------------------------------------------------------------------
// Users — students + donors + admins.
// ---------------------------------------------------------------------------
const _MOCK_USERS = [
  { id: "u-001", role: "student", name: "Maya R.", email: "maya@stanford.edu", school: "Stanford", status: "verified", joined: "2025-11-04", campaigns: 1, raised: 1680 },
  { id: "u-002", role: "student", name: "Andre W.", email: "andre@howard.edu", school: "Howard", status: "verified", joined: "2025-11-12", campaigns: 1, raised: 940 },
  { id: "u-003", role: "student", name: "Priya S.", email: "priya.s@wsu.edu", school: "WSU", status: "pending", joined: "2026-02-24", campaigns: 1, raised: 0 },
  { id: "u-004", role: "donor", name: "The Bell Family", email: "bells@example.com", status: "active", joined: "2025-08-15", gifts: 6, given: 5250 },
  { id: "u-005", role: "donor", name: "Codrington Foundation", email: "grants@codrington.org", status: "active", joined: "2025-07-30", gifts: 12, given: 42500 },
  { id: "u-006", role: "donor", name: "K. Sparks", email: "k.sparks@example.com", status: "active", joined: "2026-01-08", gifts: 3, given: 300 },
  { id: "u-007", role: "admin", name: "Terri James", email: "terri@myrichaunt.org", status: "active", joined: "2025-01-01" },
  { id: "u-008", role: "admin", name: "Ops — Jasmine", email: "jasmine@myrichaunt.org", status: "active", joined: "2025-06-10" },
];

// ---------------------------------------------------------------------------
// Homepage curation — which campaigns are featured on the landing page.
// The 3 featured slots on the landing (see FeaturedCampaigns.jsx) will read
// this list once the backend exists; today they render everything.
// ---------------------------------------------------------------------------
export const FEATURED_ORDER = [0, 1, 2]; // indices into CAMPAIGNS

// ---------------------------------------------------------------------------
// Report presets — dropdown options for the Reports view.
// ---------------------------------------------------------------------------
export const REPORT_PRESETS = [
  { id: "totals-raised", label: "Totals raised", desc: "Sum of gifts across all campaigns for the selected window." },
  { id: "avg-gift", label: "Average gift", desc: "Mean donation size. Also breaks out median and top decile." },
  { id: "avg-request", label: "Average request", desc: "Mean student goal by campaign category." },
  { id: "top-schools", label: "Top schools", desc: "Schools by campaigns launched and total raised." },
  { id: "top-categories", label: "Top categories", desc: "Fund categories by request volume and total funded." },
  { id: "verification-sla", label: "Verification SLA", desc: "Median and P95 days-to-verify by month." },
  { id: "top-donors", label: "Top donors", desc: "Donor leaderboard by gift total (month / quarter / year)." },
];

export const CAMPAIGN_ROSTER = CAMPAIGNS.map((c, idx) => ({ idx, ...c }));

// ---------------------------------------------------------------------------
// Board-safe exports — flip SHOW_MOCK_ADMIN_DATA at the top of this file to
// swap in the sample rosters above during internal previews.
// ---------------------------------------------------------------------------
export const PENDING_VERIFICATIONS = SHOW_MOCK_ADMIN_DATA ? _MOCK_PENDING_VERIFICATIONS : [];
export const ENROLLMENT_CHANGES    = SHOW_MOCK_ADMIN_DATA ? _MOCK_ENROLLMENT_CHANGES    : [];
export const PAYOUTS               = SHOW_MOCK_ADMIN_DATA ? _MOCK_PAYOUTS               : [];
export const RECENT_DONATIONS      = SHOW_MOCK_ADMIN_DATA ? _MOCK_RECENT_DONATIONS      : [];
export const USERS                 = SHOW_MOCK_ADMIN_DATA ? _MOCK_USERS                 : [];
